import { historyForCohortQuery, mrrQuery, reportingMarginQuery } from "@/api/reporting";
import { Box, Flex } from "@givenwell/components";
import { oklch } from "@givenwell/components/src/utils/color";
import { colors, fonts } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/dashboard/tv2")({
  component: RouteComponent,
});

const start = new Date();
start.setMonth(start.getMonth() - 12);
start.setDate(1);
start.setHours(0, 0, 0, 0);
const end = new Date();
end.setMonth(end.getMonth() + 1);
end.setDate(0);
end.setHours(23, 59, 59, 999);

const months = Array.from({ length: 13 }).map((_, i) => {
  const date = new Date(start);
  date.setMonth(start.getMonth() + i);
  return date;
});

const monthFmt = new Intl.DateTimeFormat("en-NZ", { month: "short", year: "2-digit" });

const ROW_GAP = 5;
const COLUMN_GAP = 20;
const HEADER_COLUMN_WIDTH = 100;

type Column = {
  name: string;
  Cell: (props: { month: Date }) => React.ReactNode;
};

const dollarFmt = new Intl.NumberFormat("en-NZ", {
  style: "currency",
  currency: "NZD",
  currencyDisplay: "narrowSymbol",
  maximumSignificantDigits: 3,
  maximumFractionDigits: 1,
  notation: "compact",
  roundingMode: "floor",
});

const percentFmt = new Intl.NumberFormat("en-NZ", {
  style: "percent",
  maximumFractionDigits: 0,
  roundingMode: "floor",
});

const COLUMNS: Column[] = [
  {
    name: "MRR",
    Cell: ({ month }) => {
      const value = Math.random();
      const { data } = useQuery(
        mrrQuery({
          amortizeOneOffs: true,
          amortizeRecurring: true,
          endDate: end,
          includeGivenwell: false,
          tokenCurrency: "nztoken",
        }),
      );
      const allMonths = months.map(month => {
        const relevantPoints =
          data?.filter(m => {
            const d = new Date(m.month);
            return d.getFullYear() === month.getFullYear() && d.getMonth() === month.getMonth();
          }) || [];
        return {
          month: month,
          total: relevantPoints.reduce((acc, curr) => acc + (curr?.total || 0), 0),
        };
      });

      const bestMonth = Math.max(...allMonths.map(m => m.total));
      const thisMonth = allMonths.find(m => m.month.getTime() === month.getTime())?.total || 0;
      const progress = bestMonth === 0 ? 0 : thisMonth / bestMonth;

      return <Cell progress={progress}>{dollarFmt.format(thisMonth / 100)}</Cell>;
    },
  },
  {
    name: "12 Month Utilisation",
    Cell: ({ month }) => {
      const cohortDate = new Date(month);
      cohortDate.setMonth(cohortDate.getMonth() - 11);

      const measurementDate = new Date(month);
      measurementDate.setMonth(measurementDate.getMonth() + 1);
      measurementDate.setDate(0);
      measurementDate.setHours(23, 59, 59, 999);
      const measurementDateString = `${measurementDate.getFullYear()}-${(measurementDate.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${measurementDate.getDate().toString().padStart(2, "0")}`;

      const q = useQuery(
        historyForCohortQuery({
          cohort: `${cohortDate.getFullYear()}-${(cohortDate.getMonth() + 1).toString().padStart(2, "0")}`,
          includeGivenwellSupporters: false,
          supporterIds: undefined,
          tokenCurrencies: ["nztoken"],
        }),
      );

      const snapshotsForThisMonth = q.data?.snapshots.filter(snapshot => {
        return (
          snapshot.snapshot_date_from <= measurementDateString && snapshot.snapshot_date_to >= measurementDateString
        );
      });
      const totalTokens = snapshotsForThisMonth?.reduce((acc, curr) => acc + (curr.total_tokens || 0), 0) || 0;
      const totalSpent = snapshotsForThisMonth?.reduce((acc, curr) => acc + (curr.total_spent || 0), 0) || 0;

      const utilisationRate = totalTokens === 0 ? 0 : totalSpent / totalTokens;

      const progress = convertToDivergingColor(utilisationRate, 0.95);

      return <Cell progress={utilisationRate === 0 ? 0 : progress}>{percentFmt.format(utilisationRate)}</Cell>;
    },
  },
  {
    name: "6 Month Utilisation",
    Cell: ({ month }) => {
      const cohortDate = new Date(month);
      cohortDate.setMonth(cohortDate.getMonth() - 5);

      const measurementDate = new Date(month);
      measurementDate.setMonth(measurementDate.getMonth() + 1);
      measurementDate.setDate(0);
      measurementDate.setHours(23, 59, 59, 999);
      const measurementDateString = `${measurementDate.getFullYear()}-${(measurementDate.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${measurementDate.getDate().toString().padStart(2, "0")}`;

      const q = useQuery(
        historyForCohortQuery({
          cohort: `${cohortDate.getFullYear()}-${(cohortDate.getMonth() + 1).toString().padStart(2, "0")}`,
          includeGivenwellSupporters: false,
          supporterIds: undefined,
          tokenCurrencies: ["nztoken"],
        }),
      );

      const snapshotsForThisMonth = q.data?.snapshots.filter(snapshot => {
        return (
          snapshot.snapshot_date_from <= measurementDateString && snapshot.snapshot_date_to >= measurementDateString
        );
      });
      const totalTokens = snapshotsForThisMonth?.reduce((acc, curr) => acc + (curr.total_tokens || 0), 0) || 0;
      const totalSpent = snapshotsForThisMonth?.reduce((acc, curr) => acc + (curr.total_spent || 0), 0) || 0;

      const utilisationRate = totalTokens === 0 ? 0 : totalSpent / totalTokens;

      const progress = convertToDivergingColor(utilisationRate, 0.5);

      return <Cell progress={utilisationRate === 0 ? 0 : progress}>{percentFmt.format(utilisationRate)}</Cell>;
    },
  },
  {
    name: "Marketplace Share",
    Cell: ({ month }) => {
      const { data } = useQuery(
        reportingMarginQuery(
          `${start.getFullYear().toString().padStart(2, "0")}-${(start.getMonth() + 1).toString().padStart(2, "0")}`,
          `${end.getFullYear().toString().padStart(2, "0")}-${(end.getMonth() + 1).toString().padStart(2, "0")}`,
          "nzd",
        ),
      );
      const months = data?.months || [];
      const monthString = `${month.getFullYear().toString().padStart(2, "0")}-${(month.getMonth() + 1)
        .toString()
        .padStart(2, "0")}`;

      const thisMonth = months.find(m => {
        return m.month === monthString;
      });

      const percent = thisMonth?.marketplace.percent || 0;

      const progress = convertToDivergingColor(percent, 0.8);

      return <Cell progress={progress}>{percentFmt.format(percent)}</Cell>;
    },
  },
];

/**
 * Maps value in range 0-1 to -1-1, setting midpoint to 0
 */
function convertToDivergingColor(value: number, midpoint: number) {
  let progress = value;
  if (value >= midpoint) {
    progress = (value - midpoint) / (1 - midpoint);
  } else {
    const gap = midpoint - value;
    progress = -gap / midpoint;
  }
  return progress;
}

function RouteComponent() {
  return (
    <Flex
      css={{
        flexDirection: "column",
        width: "100%",
        height: "100%",
        gap: ROW_GAP,
      }}
    >
      <Flex css={{ gap: COLUMN_GAP }}>
        <Box css={{ textAlign: "right", width: HEADER_COLUMN_WIDTH }}></Box>
        {COLUMNS.map(column => (
          <Box key={column.name} css={{ flex: "1 0 0px", textAlign: "center" }}>
            <Box css={{ fontWeight: "bold", fontFamily: fonts.ftHabit }}>{column.name}</Box>
          </Box>
        ))}
      </Flex>
      {months.map((month, i) => (
        <Flex
          key={month.toString()}
          css={{
            flex: "1 0 0px",
            gap: COLUMN_GAP,
            borderTop: i === months.length - 1 ? `1px solid ${colors.outline}` : "none",
            paddingTop: i === months.length - 1 ? ROW_GAP : 0,
          }}
        >
          <Box
            css={{
              textAlign: "right",
              width: HEADER_COLUMN_WIDTH,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {monthFmt.format(month)}
          </Box>
          {COLUMNS.map(column => (
            <column.Cell key={column.name} month={month} />
          ))}
        </Flex>
      ))}
    </Flex>
  );
}

const lerp = (start: number, end: number, factor: number): number => {
  return start + (end - start) * factor;
};

type LCH = [number, number, number];

function interpolateColor(start: LCH, end: LCH, factor: number) {
  const lightness = lerp(start[0], end[0], factor);
  const chroma = lerp(start[1], end[1], factor);
  const hue = lerp(start[2], end[2], factor);
  return [oklch(lightness, chroma, hue), lightness] as const;
}

const cream: LCH = [0.9797, 0.0118, 265.49];
const blue: LCH = [0.5217, 0.2665, 265.49];
const red: LCH = [0.6275, 0.2645, 29.234];
const lightred: LCH = [0.9797, 0.0118, 29.234];
function getColor(progress: number) {
  if (progress < 0) {
    return interpolateColor(lightred, red, 0 - progress);
  } else {
    return interpolateColor(cream, blue, progress);
  }
}

function Cell({ progress, children }: { progress: number; children: React.ReactNode }) {
  const [color, lightness] = getColor(progress);
  return (
    <Box
      css={{
        flex: "1 0 0px",
        borderRadius: 9,
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: fonts.ftHabit,
        fontScale: "xl",
      }}
      style={{
        backgroundColor: color,
        color: lightness < 0.65 ? "white" : "black",
      }}
    >
      {children}
    </Box>
  );
}
